import { memo, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { color, border } from 'styled-system';
import { motion, HTMLMotionProps } from 'motion/react';

interface ButtonProps extends HTMLMotionProps<'button'> {
	auto?: boolean;
	smallfont?: boolean;
	small?: boolean;
	regularfont?: boolean;
	$customFont?: string;
	disabled?: boolean;
	dataAt?: string;
	isEffectDisabled?: boolean;
}

const StyledButton = styled(motion.button)``;
const WHILE_TAP = { scale: 0.9 };

export const Effect = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
	<StyledButton
		ref={ref}
		whileTap={props.disabled || props.isEffectDisabled ? undefined : WHILE_TAP}
		data-at={props.dataAt}
		{...props}
	>
		{props.children}
	</StyledButton>
));

Effect.displayName = 'Button';

export const Button = memo(styled(Effect)`
	border-radius: ${props => (props.auto ? '3px' : 'var(--button-border-radius, 3px)')};
	font: ${props => (props.smallfont ? 'var(--small)' : 'var(--h3)')};
	padding: ${props => (props.small ? '8px 40px' : '14px')};
	text-align: center;
	white-space: nowrap;
	width: ${props => (props.auto ? 'auto' : '100%')};
	position: relative;
	${props =>
		props.auto &&
		css`
			width: auto;
			padding: 9px 9px 9px 9px;
		`};
	&:focus {
		outline: none;
	}
	&:disabled {
		background-color: var(--button-disabled-bg);
		color: var(--button-disabled-text);
		cursor: not-allowed;
	}
	${color}
`);

export const ButtonPrimary = memo(styled(Button)`
	background-color: var(--button-primary-bg, rgba(var(--primary), 1));
	color: var(--button-primary-text, rgba(var(--base-02), 1));
	text-transform: var(--brand-text-transform, none);
	> svg {
		height: 24px;
		vertical-align: middle;
		display: inline-block;
		padding-right: 12px;
	}
`);

export const ButtonSecondary = memo(styled(Button)`
	background-color: var(--button-secondary-bg, rgba(var(--base-08), 1));
	font: var(--label-regular);
`);

export const ButtonTransparent = memo(styled(Button)`
	background-color: var(--transparent-bg, rgba(0, 0, 0, 0.4));
`);

export const ButtonWithoutBackground = memo(styled(Button)`
	background-color: transparent;
	color: inherit;
`);

export const ButtonRounded = memo(styled(ButtonPrimary)`
	width: auto;
	border-radius: 26px;
	font: ${props => (props.smallfont ? 'var(--label-bold)' : 'var(--h3)')};
`);

export const ButtonRoundedSecondary = memo(styled(ButtonRounded)`
	background-color: var(--button-secondary-bg, rgba(var(--base-08), 1));
	color: var(--button-secondary-text, rgba(var(--base-02), 1));
	text-transform: none;
`);

export const ButtonRoundedPrimary = styled(ButtonRounded)`
	width: 100%;
	font: ${props => (props.regularfont ? 'var(--label-regular)' : 'var(--h3)')};
`;

export const ButtonOutlined = styled(ButtonWithoutBackground)`
	color: rgba(var(--secondary), 1);
	border: 1px solid rgba(var(--secondary), 1);
	font: ${props => (props.$customFont ? props.$customFont : 'var(--label-regular)')};
	${border}
`;

export const ButtonOutlinedRounded = styled(ButtonOutlined)`
	border-radius: 26px;
	${color}
	${border}
`;

export const ButtonRoundedSecondaryFullWidth = memo(styled(ButtonRoundedSecondary)`
	font: var(--label-regular);
	padding: 6px 40px;
	width: 100%;
`);

export const ButtonRoundedSmall = memo(styled(ButtonRounded)`
	padding: 6px 20px;
	width: 90%;
`);

export const ButtonOutlinedRoundedSmall = memo(styled(ButtonOutlined)`
	padding: 2px 12px;
`);

// TODO: Button with posed effect for svg icons.
export const ButtonIcon = memo(styled(Button)`
	width: auto;
	height: auto;
	padding: 0;
	border-radius: 0;
`);

export const ButtonIconBg = memo(styled(ButtonIcon)`
	padding: 4px;
	border-radius: 50%;
	background-color: rgba(var(--opacity-overlay, var(--game-info-exit-bg-color)), 0.8);
`);

export const BackButtonIconBg = memo(styled(ButtonIconBg)`
	> svg {
		margin-left: 0;
		margin-right: -1px;
	}
`);

export const ButtonText = memo(styled(Button)`
	width: auto;
	height: auto;
	padding: 0;
	border-radius: 0;

	color: rgba(var(--primary), 1);
`);

const ButtonSmall = memo(styled(Effect)`
	font: var(--label-bold);
	padding: 8px 24px;
	text-align: center;
	white-space: nowrap;
	border-radius: var(--button-small-border-radius, 100px);
	width: ${props => (props.auto ? 'auto' : '100%')};
	&:focus {
		outline: none;
	}
	&:disabled {
		background-color: var(--button-disabled-bg);
		color: var(--button-disabled-text);
		cursor: not-allowed;
	}
`);

export const ButtonSmallPrimary = memo(styled(ButtonSmall)`
	background-color: var(--button-primary-bg, rgba(var(--primary), 1));
	color: var(--button-primary-text, rgba(var(--base-02), 1));
	text-transform: var(--brand-text-transform, none);
`);

export const ButtonSmallSecondary = memo(styled(ButtonSmall)`
	background-color: var(--button-secondary-bg, rgba(var(--base-08), 1));
	color: var(--button-secondary-text, rgba(var(--base-02), 1));
`);

export const ButtonSmallOutlined = memo(styled(ButtonSmall)`
	color: rgba(var(--secondary), 1);
	border: 1px solid rgba(var(--secondary), 1);
`);

interface DotProps {
	active?: boolean;
}

export const Dot = styled.button<DotProps>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${props =>
		props.active
			? 'var(--button-dot-active-bg, rgba(var(--secondary), 1))'
			: 'var(--button-dot-bg, var(--button-secondary-bg, rgba(var(--base-08), 1)))'};
`;
